import {
	Button,
	Checkbox,
	HStack,
	Input,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { RequestStatus } from '@montel/montelpro-shared-components/enums';
import { ACCOUNT_API_COMPANIES } from 'apiRoutes';
import { useGlobalContext } from 'components/common/GlobalContext/GlobalContext';
import { MultiInput } from 'components/common/MultiInput';
import useSubmit, { POST } from 'hooks/useSubmit';
import useAuthRoles from 'hooks/useAuthRoles';
import { AdminRole } from 'domain/account/enums/AdminRole';
import { FunctionComponent, useState } from 'react';
import {
	validateCompanyName,
	validateCountryCode,
	validateDomain,
	validateYouniumId,
} from './companyValidation';
import styled from 'styled-components';
import { Company } from 'domain/company/Company';
import CountrySelect from 'components/common/CountrySelect';

export interface CreateCompanyFormState {
	name: string;
	emailDomains: string[];
	countryCode: string;
	youniumId: string;
	hubspotId: string;
	youniumAccountNumber: string;
	isCustomer: boolean;
}

interface CreateCompanyFormProps {
	initialFormState: CreateCompanyFormState;
	onSuccess: (company: Company) => void;
	onCancel: () => void;
}

const StyledMultiInput = styled(MultiInput)`
	width: 50%;
`;

const FormActions = styled(HStack)`
	margin-top: 2rem;
`;

const CreateCompanyForm: FunctionComponent<CreateCompanyFormProps> = ({
	initialFormState,
	onSuccess,
	onCancel,
}) => {
	const [formState, setFormState] =
		useState<CreateCompanyFormState>(initialFormState);

	const [errors, setErrors] = useState<{
		name?: string;
		emailDomains?: string;
		country?: string;
		youniumId?: string;
		hubspotId?: string;
		youniumAccountNumber?: string;
	}>({});

	const { countries } = useGlobalContext();
	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();

	const [submit, status] = useSubmit(
		`${ACCOUNT_API_COMPANIES}/CreateCompany`,
		POST,
		{
			messageOnSuccess: 'Company created successfully',
			messageOnError: 'Error creating company',
			showApiError: true,
		},
	);

	const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (errors.name) {
			setErrors({
				...errors,
				name: undefined,
			});
		}

		setFormState({
			...formState,
			name: e.target.value,
		});
	};

	const onCountryChange = (countryCode: string) => {
		setErrors({
			...errors,
			country: undefined,
		});

		setFormState({
			...formState,
			countryCode: countryCode,
		});
	};

	const onYouniumIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (errors.youniumId) {
			setErrors({
				...errors,
				youniumId: undefined,
			});
		}
		setFormState({
			...formState,
			youniumId: e.target.value,
		});
	};

	const onYouniumAccountNumberChange = (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		if (errors.youniumAccountNumber) {
			setErrors({
				...errors,
				youniumAccountNumber: undefined,
			});
		}
		setFormState({
			...formState,
			youniumAccountNumber: e.target.value,
		});
	};

	const onEmailDomainsChange = (values: string[]) => {
		setFormState({
			...formState,
			emailDomains: values,
		});
	};

	const validateNameField = () => {
		setErrors({
			...errors,
			name: validateCompanyName(formState.name),
		});
	};

	const validateCountryField = () => {
		setErrors({
			...errors,
			country: validateCountryCode(formState.countryCode),
		});
	};

	const validateYouniumIdField = () => {
		setErrors({
			...errors,
			youniumId: validateYouniumId(formState.youniumId),
		});
	};

	const onSubmit = async () => {
		const result = await submit({
			companyName: formState.name,
			countryCode: formState.countryCode,
			emailDomains: formState.emailDomains.length
				? formState.emailDomains
				: null,
			youniumId: formState.youniumId || null,
			hubspotId: formState.hubspotId || null,
			youniumAccountNumber: formState.youniumAccountNumber || null,
			isCustomer: formState.isCustomer,
		});

		if (result.success) {
			setFormState(initialFormState);
			setErrors({});
			onSuccess &&
				onSuccess({
					id: result.data,
					name: formState.name,
					country: countries.find(
						(country) =>
							country.twoLetterIsoCode === formState.countryCode,
					),
					emailDomains: formState.emailDomains,
					youniumId: formState.youniumId,
					hubspotId: formState.hubspotId,
					isCustomer: formState.isCustomer,
				} as Company);
		}
	};

	const isValid = !Boolean(
		errors.name ||
			formState.name.length < 3 ||
			errors.country ||
			errors.emailDomains ||
			errors.youniumId ||
			errors.hubspotId,
	);
	const hasValues = Boolean(formState.name && formState.countryCode);

	return (
		<VStack gap="md" fullWidth>
			<HStack gap="md" fullWidth wrap="nowrap">
				<Input
					required
					name="name"
					label="Company Name"
					value={formState.name}
					onChange={(e) => onNameChange(e)}
					onBlur={validateNameField}
					placeholder="Enter company name"
					errorMessage={errors.name && errors.name}
				/>
				<CountrySelect
					required
					value={formState.countryCode}
					onChange={onCountryChange}
					onBlur={validateCountryField}
				/>
			</HStack>
			<HStack gap="md" fullWidth wrap="nowrap">
				<StyledMultiInput
					label="Email Domains"
					values={formState.emailDomains}
					setValue={onEmailDomainsChange}
					validateFn={validateDomain}
					error={errors.emailDomains}
					setError={(error) => {
						setErrors({
							...errors,
							emailDomains: error,
						});
					}}
				/>
			</HStack>
			<HStack gap="md" fullWidth wrap="nowrap">
				<Input
					name="youniumId"
					label="Younium ID (optional)"
					value={formState.youniumId}
					onChange={onYouniumIdChange}
					onBlur={validateYouniumIdField}
					placeholder="Enter Younium ID"
					errorMessage={errors.youniumId && errors.youniumId}
				/>
				<Input
					name="youniumAccountNumber"
					label="Younium Account Number (optional)"
					value={formState.youniumAccountNumber}
					onChange={onYouniumAccountNumberChange}
					placeholder="Enter Younium Account Number"
					errorMessage={
						errors.youniumAccountNumber &&
						errors.youniumAccountNumber
					}
				/>
			</HStack>
			<HStack gap="md" fullWidth wrap="nowrap">
				<Input
					name="hubspotId"
					label="HubSpot ID (optional)"
					type="number"
					value={formState.hubspotId}
					onChange={(e) =>
						setFormState({
							...formState,
							hubspotId: e.target.value,
						})
					}
					placeholder="Enter Hubspot ID"
					errorMessage={errors.hubspotId && errors.hubspotId}
				/>
			</HStack>
			<HStack gap="md" fullWidth wrap="nowrap">
				<Checkbox
					label="This company is a customer"
					size="md"
					checked={formState.isCustomer}
					onChange={(e) =>
						setFormState({
							...formState,
							isCustomer: e.target.checked,
						})
					}
				/>
			</HStack>

			<FormActions fullWidth justify="flex-end">
				<Button
					type="submit"
					text="Create Company"
					size="md"
					variant="primary"
					onClick={onSubmit}
					isLoading={status === RequestStatus.LOADING}
					loadingText="Creating Company..."
					isDisabled={
						!isValid ||
						!hasValues ||
						status === RequestStatus.LOADING ||
						isRestrictedToRole(AdminRole.SALES)
					}
				/>
				<Button
					variant="secondary"
					text="Cancel"
					size="md"
					onClick={onCancel}
				/>
			</FormActions>
		</VStack>
	);
};

export default CreateCompanyForm;
